import type { LocalDate } from './date';
import type { ProblemDrafts } from './problem-drafts';

/** progress / settings / drafts / lessons の値の現行の版（progress-store.md §6.3）。 */
export const CURRENT_VERSION = 1;

/** エクスポートファイルの現行の版（§6.3）。2 で lessons を足した。値の版とは別に数える。 */
export const EXPORT_VERSION = 2;

export type Box = 1 | 2 | 3 | 4 | 5;

/** progress-store.md §3。 */
export interface CardRecord {
  box: Box;
  /** この日以降に復習に出る。 */
  due: LocalDate;
  firstSeen: LocalDate;
  /** 箱の遷移を最後に起こした日。 */
  lastEvaluated: LocalDate;
  seen: number;
  correct: number;
}

export type AnswerResult = 'correct' | 'wrong';

export interface HistoryEntry {
  /** ISO 8601（UTC）。 */
  at: string;
  date: LocalDate;
  cardId: string;
  result: AnswerResult;
  hint: boolean;
  evaluated: boolean;
  verdict?: 'AC' | 'WA' | 'RE' | 'TLE';
  preflightMatched?: boolean;
}

export interface ProgressState {
  version: 1;
  cards: Record<string, CardRecord>;
  activity: Record<LocalDate, number>;
  history: HistoryEntry[];
}

export interface Settings {
  version: 1;
  dailyReviewLimit?: number;
  dailyNewLimit?: number;
  motion?: 'system' | 'reduce' | 'full';
  lastExportAt?: string;
}

export interface DraftItem {
  code: string;
  updatedAt: string;
}

export interface Drafts {
  version: 1;
  items: Record<string, DraftItem>;
}

/** レッスン 1 つの進捗（progress-store.md §7、lesson-format.md §8.2）。 */
export interface LessonRecord {
  /** 最初に開いた日。 */
  firstOpened: LocalDate;
  /** 予想を出して実行し比べた実験の id（済ませた順）。 */
  experiments: string[];
  /** AC か「模範解答を見る」になった書く課題の id（済ませた順）。 */
  writes: string[];
  /** 完了した日。一度付いたら消さない。 */
  completed?: LocalDate;
}

export interface LessonProgress {
  version: 1;
  lessons: Record<string, LessonRecord>;
}

/** progress-store.md §6.1。 */
export interface ExportFile {
  format: 'inductor-class-export';
  version: 2;
  exportedAt: string;
  progress: ProgressState;
  settings: Settings;
  drafts: Drafts;
  lessons: LessonProgress;
  /**
   * 問題の書きかけ（problem-format.md §8）。version 2 のまま任意で足した。無いファイルは空として読む。
   * 検証は src/lib/progress/problem-drafts.ts（schema の外。progress-store.md §9）。
   */
  problemDrafts?: ProblemDrafts;
}

export const EXPORT_FORMAT = 'inductor-class-export';

/** localStorage のキー（§1）。 */
export const STORAGE_KEYS = {
  progress: 'inductor-class.progress',
  settings: 'inductor-class.settings',
  drafts: 'inductor-class.drafts',
  lessons: 'inductor-class.lessons',
  problemDrafts: 'inductor-class.problem-drafts',
  backup: 'inductor-class.backup',
} as const;

export const CORRUPT_KEY_PREFIX = 'inductor-class.corrupt.';

export const HISTORY_LIMIT = 2000;
export const ACTIVITY_DAYS = 400;
export const DRAFTS_LIMIT = 200;
export const DEFAULT_DAILY_REVIEW_LIMIT = 20;
export const DEFAULT_DAILY_NEW_LIMIT = 5;

export function emptyProgress(): ProgressState {
  return { version: 1, cards: {}, activity: {}, history: [] };
}

export function emptySettings(): Settings {
  return { version: 1 };
}

export function emptyDrafts(): Drafts {
  return { version: 1, items: {} };
}

export function emptyLessonProgress(): LessonProgress {
  return { version: 1, lessons: {} };
}
